import React, { useState } from 'react';
import { MessageSquare, Download, FileText } from 'lucide-react';
import { Button, Card, FileUploadZone, ProgressBar, Alert, Badge, PDFPreview, ToolActionButtons } from '../shared';
import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import { saveAs } from 'file-saver';
import { toast } from 'react-toastify';
import '../../styles/design-system.css';

const AnnotatePDF = () => {
    const [file, setFile] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [progress, setProgress] = useState(0);
    const [pageCount, setPageCount] = useState(0);
    const [annotation, setAnnotation] = useState('');
    const [pageNumber, setPageNumber] = useState(1);
    const [previewBlob, setPreviewBlob] = useState(null);
    const [previewFilename, setPreviewFilename] = useState('');

    const handleFileSelected = async (files) => {
        if (files && files.length > 0) {
            const selectedFile = files[0];
            setFile(selectedFile);

            try {
                const arrayBuffer = await selectedFile.arrayBuffer();
                const pdf = await PDFDocument.load(arrayBuffer);
                setPageCount(pdf.getPageCount());
                toast.success(`PDF loaded: ${pdf.getPageCount()} pages`);
            } catch (error) {
                toast.error(`Failed to load PDF: ${error.message}`);
            }
        }
    };

    const addAnnotation = async () => {
        if (!file || !annotation) {
            toast.warning('Please enter annotation text');
            return;
        }

        try {
            setIsProcessing(true);
            setProgress(10);

            const arrayBuffer = await file.arrayBuffer();
            const pdfDoc = await PDFDocument.load(arrayBuffer);
            const font = await pdfDoc.embedFont(StandardFonts.Helvetica);

            setProgress(50);

            const pages = pdfDoc.getPages();
            const page = pages[pageNumber - 1];
            const { width, height } = page.getSize();

            // Add annotation as a comment box
            page.drawRectangle({
                x: 50,
                y: height - 150,
                width: 200,
                height: 80,
                borderColor: rgb(1, 0.8, 0),
                borderWidth: 2,
                color: rgb(1, 1, 0.9),
                opacity: 0.8
            });

            page.drawText(annotation, {
                x: 60,
                y: height - 100,
                size: 10,
                font,
                color: rgb(0, 0, 0),
                maxWidth: 180
            });

            setProgress(90);

            const pdfBytes = await pdfDoc.save();
            const blob = new Blob([pdfBytes], { type: 'application/pdf' });

            setProgress(100);

            const fileName = file.name.replace(/\.pdf$/i, '');
            const filename = `${fileName}_annotated.pdf`;

            // Set preview
            setPreviewBlob(blob);
            setPreviewFilename(filename);

            toast.success('Annotation added! Opening preview...');

            setTimeout(() => setProgress(0), 1000);

        } catch (error) {
            console.error('Error:', error);
            toast.error(`Failed to add annotation: ${error.message}`);
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <div>
            {/* Action Buttons */}
            <ToolActionButtons
                onAdd={() => {
                    document.querySelector('input[type="file"]')?.click();
                }}
                notificationCount={file ? 1 : 0}
            />

            <div className="tool-header">
                <h1 className="tool-title">
                    <MessageSquare size={36} style={{ display: 'inline', marginRight: 'var(--spacing-3)', color: 'var(--color-accent)' }} />
                    Annotate PDF
                </h1>
                <p className="tool-description">
                    Add comments and annotations to your PDF documents.
                </p>
            </div>

            {!file ? (
                <FileUploadZone
                    onFilesSelected={handleFileSelected}
                    acceptedTypes={['application/pdf']}
                    multiple={false}
                    title="Drop PDF file here"
                    description="or click to browse"
                />
            ) : (
                <Alert variant="success" title="PDF Loaded">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
                            <FileText size={16} />
                            <span>{file.name}</span>
                            {pageCount > 0 && <Badge variant="info">{pageCount} pages</Badge>}
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => { setFile(null); setPageCount(0); }}>
                            Change PDF
                        </Button>
                    </div>
                </Alert>
            )}

            {file && (
                <>
                    <div style={{ marginTop: 'var(--spacing-6)' }}>
                        <Card>
                            <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-bold)', marginBottom: 'var(--spacing-4)' }}>
                                Annotation Settings
                            </h3>

                            <div style={{ marginBottom: 'var(--spacing-4)' }}>
                                <label className="label">Page Number</label>
                                <input
                                    type="number"
                                    value={pageNumber}
                                    onChange={(e) => setPageNumber(Math.max(1, Math.min(pageCount, parseInt(e.target.value) || 1)))}
                                    min="1"
                                    max={pageCount}
                                    className="input"
                                />
                            </div>

                            <div>
                                <label className="label">Annotation Text</label>
                                <textarea
                                    value={annotation}
                                    onChange={(e) => setAnnotation(e.target.value)}
                                    className="input"
                                    rows={4}
                                    placeholder="Enter your comment or note..."
                                />
                            </div>
                        </Card>
                    </div>

                    {isProcessing && (
                        <div style={{ marginTop: 'var(--spacing-6)' }}>
                            <ProgressBar value={progress} striped showLabel />
                        </div>
                    )}

                    <div style={{ marginTop: 'var(--spacing-6)' }}>
                        <Button variant="primary" size="lg" onClick={addAnnotation} disabled={isProcessing || !annotation} loading={isProcessing} fullWidth>
                            <Download size={20} />
                            Add Annotation
                        </Button>
                    </div>
                </>
            )}

            {/* PDF Preview Modal */}
            {previewBlob && (
                <PDFPreview
                    pdfBlob={previewBlob}
                    filename={previewFilename}
                    onClose={() => setPreviewBlob(null)}
                    onDownload={() => {
                        if (previewBlob && previewFilename) {
                            saveAs(previewBlob, previewFilename);
                            toast.success('Download started!');
                            setPreviewBlob(null);
                        }
                    }}
                />
            )}
        </div>
    );
};

export default AnnotatePDF;
